'use client';

import { useEffect } from 'react';

/**
 * Progressive motion for server-rendered landing markup.
 *
 * - `data-reveal`: content below the fold fades in once it scrolls into view.
 *   Content already on screen, and every element when JavaScript or motion is
 *   off, stays visible from the first paint.
 * - `data-motion-scope`: gets `data-active="false"` while offscreen so CSS
 *   loops pause.
 * - `data-pointer`: receives `--px` and `--py` (-1 to 1) from a fine pointer.
 * - `data-spotlight-group`: each `data-spotlight` child receives `--sx` and
 *   `--sy` in pixels for a cursor-following glow.
 */
export function LandingMotion() {
  useEffect(() => {
    const reduceMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;
    const finePointer = window.matchMedia('(pointer: fine)').matches;
    const cleanups: Array<() => void> = [];

    if (!reduceMotion && 'IntersectionObserver' in window) {
      const revealObserver = new IntersectionObserver(
        entries => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            (entry.target as HTMLElement).dataset.inview = 'true';
            revealObserver.unobserve(entry.target);
          }
        },
        { rootMargin: '0px 0px -8% 0px' }
      );
      const pending: HTMLElement[] = [];
      for (const element of document.querySelectorAll<HTMLElement>(
        '[data-reveal]'
      )) {
        // Measured from the top of the document: after a client navigation
        // the window still holds the previous page's scroll position.
        const top = element.getBoundingClientRect().top + window.scrollY;
        if (top < window.innerHeight * 0.92) continue;
        element.dataset.inview = 'false';
        pending.push(element);
      }
      // Observe after the router has reset the scroll position, so content
      // below the fold is not revealed by the previous page's offset.
      const frame = requestAnimationFrame(() => {
        for (const element of pending) revealObserver.observe(element);
      });
      cleanups.push(() => {
        cancelAnimationFrame(frame);
        revealObserver.disconnect();
      });

      const scopeObserver = new IntersectionObserver(entries => {
        for (const entry of entries) {
          (entry.target as HTMLElement).dataset.active = String(
            entry.isIntersecting
          );
        }
      });
      for (const scope of document.querySelectorAll('[data-motion-scope]')) {
        scopeObserver.observe(scope);
      }
      cleanups.push(() => scopeObserver.disconnect());
    }

    if (!reduceMotion && finePointer) {
      for (const element of document.querySelectorAll<HTMLElement>(
        '[data-pointer]'
      )) {
        let frame = 0;
        const onMove = (event: PointerEvent) => {
          cancelAnimationFrame(frame);
          frame = requestAnimationFrame(() => {
            const rect = element.getBoundingClientRect();
            const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
            const y = ((event.clientY - rect.top) / rect.height) * 2 - 1;
            element.style.setProperty('--px', x.toFixed(3));
            element.style.setProperty('--py', y.toFixed(3));
          });
        };
        const onLeave = () => {
          cancelAnimationFrame(frame);
          element.style.setProperty('--px', '0');
          element.style.setProperty('--py', '0');
        };
        element.addEventListener('pointermove', onMove, { passive: true });
        element.addEventListener('pointerleave', onLeave);
        cleanups.push(() => {
          cancelAnimationFrame(frame);
          element.removeEventListener('pointermove', onMove);
          element.removeEventListener('pointerleave', onLeave);
        });
      }

      for (const group of document.querySelectorAll<HTMLElement>(
        '[data-spotlight-group]'
      )) {
        const cards = Array.from(
          group.querySelectorAll<HTMLElement>('[data-spotlight]')
        );
        let frame = 0;
        const onMove = (event: PointerEvent) => {
          cancelAnimationFrame(frame);
          frame = requestAnimationFrame(() => {
            for (const card of cards) {
              const rect = card.getBoundingClientRect();
              card.style.setProperty('--sx', `${event.clientX - rect.left}px`);
              card.style.setProperty('--sy', `${event.clientY - rect.top}px`);
            }
          });
        };
        group.addEventListener('pointermove', onMove, { passive: true });
        cleanups.push(() => {
          cancelAnimationFrame(frame);
          group.removeEventListener('pointermove', onMove);
        });
      }
    }

    const onAnchorClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }
      const link = (event.target as Element | null)?.closest<HTMLAnchorElement>(
        'a[href^="#"]'
      );
      const id = link?.getAttribute('href')?.slice(1);
      const target = id ? document.getElementById(id) : null;
      if (!target) return;
      event.preventDefault();
      target.scrollIntoView({
        behavior: reduceMotion ? 'auto' : 'smooth',
        block: 'start',
      });
      history.pushState(null, '', `#${id}`);
      if (id === 'main') target.focus({ preventScroll: true });
    };
    document.addEventListener('click', onAnchorClick);
    cleanups.push(() => document.removeEventListener('click', onAnchorClick));

    return () => {
      for (const cleanup of cleanups) cleanup();
    };
  }, []);

  return null;
}
